/**
 * Registro único dos gêmeos em markdown e das rotas conhecidas do site. Quem
 * lê daqui: `middleware.ts` (negociação por `Accept` e 404 em markdown),
 * `scripts/build-agent-layer.mjs` (grava `dist/**\/*.md` e o `llms.txt`) e os
 * testes. Página com gêmeo, página sem gêmeo e caminho desconhecido só
 * concordam entre essas superfícies porque todas leem esta lista.
 */
import { COMPARISON_PAGES } from '../../content/seo/comparisons';
import { GUIDE_PAGES } from '../../content/seo/guides';
import * as SEGMENTS_MODULE from '../../content/seo/segments';
import { SEGMENT_PAGES } from '../../content/seo/segments';
import {
  aboutMarkdown,
  comparisonMarkdown,
  contactMarkdown,
  guideMarkdown,
  guidesHubMarkdown,
  homeMarkdown,
  mdPathFor,
  paymentsHelpMarkdown,
  privacyMarkdown,
  segmentMarkdown,
} from './markdown';

export interface Twin {
  /** Caminho da página HTML (canônica). */
  path: string;
  /** Caminho do gêmeo (`/index.md` para a home). */
  mdPath: string;
  render: () => string | null;
}

const twin = (path: string, render: () => string | null): Twin => ({ path, mdPath: mdPathFor(path), render });

export function listTwins(): Twin[] {
  return [
    twin('/', homeMarkdown),
    twin('/guias', guidesHubMarkdown),
    ...GUIDE_PAGES.map((g) => twin(`/guias/${g.slug}`, () => guideMarkdown(g.slug))),
    ...SEGMENT_PAGES.map((s) => twin(`/para/${s.slug}`, () => segmentMarkdown(s.slug))),
    ...COMPARISON_PAGES.map((c) => twin(`/comparar/${c.slug}`, () => comparisonMarkdown(c.slug))),
    twin('/sobre', aboutMarkdown),
    twin('/contato', contactMarkdown),
    twin('/privacidade', privacyMarkdown),
    twin('/ajuda/recebimentos', paymentsHelpMarkdown),
  ];
}

let byPath: Map<string, Twin> | null = null;
let byMdPath: Map<string, Twin> | null = null;

function index() {
  if (!byPath || !byMdPath) {
    const twins = listTwins();
    byPath = new Map(twins.map((t) => [t.path, t]));
    byMdPath = new Map(twins.map((t) => [t.mdPath, t]));
  }
  return { byPath, byMdPath };
}

/** Gêmeo da página HTML (`/guias/x` → gêmeo), ou null. */
export const twinForPath = (path: string): Twin | null => index().byPath.get(normalize(path)) ?? null;

/** Gêmeo pelo caminho `.md` pedido (`/guias/x.md`, `/index.md`), ou null. */
export const twinForMdPath = (mdPath: string): Twin | null => index().byMdPath.get(mdPath) ?? null;

export const normalize = (path: string): string => (path.length > 1 ? path.replace(/\/+$/, '') : path) || '/';

/* -------------------------------------------------------------------------- */
/* Rotas conhecidas                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Primeiro segmento de toda rota que o export gera (grupos `(…)` removidos).
 * O teste `seo.test.mjs` compara com `app/app/**` nas duas direções.
 */
export const APP_ROUTE_SEGMENTS = [
  'account',
  'agendamento',
  'ajuda',
  'appointments',
  'assistant',
  'auth',
  'become-provider',
  'booking',
  'buscar',
  'charges',
  'chat',
  'checkout',
  'client-onboarding',
  'clients',
  'comparar',
  'contato',
  'convite',
  'dev',
  'disputes',
  'enrollments',
  'enrollments-client',
  'excluir-conta',
  'expenses',
  'guias',
  'hoje',
  'home',
  'legal',
  'login',
  'onboarding',
  'para',
  'payments',
  'privacidade',
  'profile',
  'profissionais',
  'register',
  'reports',
  'reset-password',
  'retroactive',
  'return-to-app',
  'schedule',
  'service',
  'services',
  'settings',
  'sobre',
  'subscription',
  'suporte',
  'team',
  'terms',
] as const;

/**
 * Famílias geradas por `generateStaticParams`: só existe o slug do registro.
 * Os apelidos de ramo (`/para/salao`) passam porque viram redirect 308.
 */
export const REGISTRY_FAMILIES = ['guias', 'para', 'comparar'] as const;

/** Caminhos que o `vercel.json` redireciona (o redirect resolve, não é 404). */
export const REDIRECT_SOURCES = ['/legal', '/privacy', '/contact', '/about'] as const;

/**
 * Apelidos de ramo (`/para/salao` → `/para/salao-de-beleza`). Lido sem nome
 * fixo porque `SEGMENT_ALIASES` passa a ser exportado pela área de ramos.
 */
export function segmentAliases(): Record<string, string> {
  const aliases = (SEGMENTS_MODULE as Record<string, unknown>).SEGMENT_ALIASES;
  return aliases && typeof aliases === 'object' ? (aliases as Record<string, string>) : {};
}

/**
 * Caminho que alguma coisa responde (página, função, redirect). Página com
 * gêmeo já é conhecida pelo registro; isto é para as outras.
 */
export function isKnownPath(path: string, aliases: readonly string[] = Object.keys(segmentAliases())): boolean {
  const p = normalize(path);
  if (p === '/' || twinForPath(p)) return true;
  if ((REDIRECT_SOURCES as readonly string[]).includes(p)) return true;
  const parts = p.split('/').filter(Boolean);
  const first = parts[0] ?? '';
  if (first === 'api' || first.startsWith('@')) return true;
  if ((REGISTRY_FAMILIES as readonly string[]).includes(first)) {
    // Hub (`/guias`) tem gêmeo; `/para` e `/comparar` sem slug não existem.
    if (parts.length === 1) return false;
    return first === 'para' && parts.length === 2 && aliases.includes(parts[1]);
  }
  return (APP_ROUTE_SEGMENTS as readonly string[]).includes(first);
}
